import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldWrap } from "@/components/Field";
import { OnOffSwitch } from "@/components/OnOffSwitch";

const TH = "px-4 py-3";
const BLANK = { date: "", description: "", amount: "", note: "" };

function ExpenseDialog({ open, rec, coll, onClose, onSaved }) {
  const [v, setV] = useState(BLANK);
  useEffect(() => { if (open) { setV(rec ? { date: rec.date, description: rec.description, amount: rec.amount, note: rec.note || "" } : { ...BLANK, date: todayISO() }); } }, [open, rec]);
  const save = async (e) => {
    e.preventDefault();
    if (!v.date || !v.description.trim() || !(parseFloat(v.amount) > 0)) { toast.error("Please enter the date, description and amount"); return; }
    const body = { ...v, amount: parseFloat(v.amount) };
    try { await (rec ? api.put(`/special-collections/expenses/${rec.id}`, body) : api.post(`/special-collections/${coll.id}/expenses`, body)); toast.success("Expense saved"); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="special-expense-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{rec ? "Edit expense" : "Add expense"} · {coll?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="Date" required><Input type="date" className="h-11" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} data-testid="special-expense-date" /></FieldWrap>
            <FieldWrap label="Amount ($)" required><Input type="number" min="0" step="0.01" className="h-11" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} data-testid="special-expense-amount" /></FieldWrap>
          </div>
          <FieldWrap label="Description" required><Input className="h-11" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} maxLength={200} data-testid="special-expense-description" /></FieldWrap>
          <FieldWrap label="Note"><Input className="h-11" value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} maxLength={500} data-testid="special-expense-note" /></FieldWrap>
          <DialogFooter className="gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="special-expense-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SpecialExpenses({ coll, exp, admin, reload, onToggle }) {
  const [dlg, setDlg] = useState(null);
  const del = async (r) => {
    if (!window.confirm(`Delete expense "${r.description}"?`)) return;
    try { await api.delete(`/special-collections/expenses/${r.id}`); toast.success("Expense deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!exp) return null;
  return (
    <div className="space-y-4" data-testid="special-expenses">
      {admin && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <label className="flex items-center gap-3 text-sm font-semibold text-slate-900">Church users can view this Expense Report<OnOffSwitch checked={!!coll.church_expenses} onChange={onToggle} testid="special-expenses-church-toggle" /></label>
          <Button onClick={() => setDlg({})} data-testid="special-add-expense"><Plus className="mr-2 h-4 w-4" /> Add expense</Button>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        {[["income", "Total Income (paid so far)"], ["total_expenses", "Total Expenses"], ["balance", "Balance left"]].map(([k, l]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className={`mt-1 font-mono text-2xl font-bold ${k === "balance" && exp.balance < 0 ? "text-red-700" : ""}`} data-testid={`special-exp-${k}`}>{money(exp[k])}</p></div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="special-expenses-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Date", "Description", "Note", "Amount", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {!exp.expenses.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-testid="special-expenses-empty">No expenses yet.</td></tr>}
            {exp.expenses.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`special-expense-row-${r.id}`}>
                <td className={TH}>{fmtDate(r.date)}</td><td className={`${TH} font-semibold`}>{r.description}</td><td className={`${TH} text-slate-600`}>{r.note}</td><td className={`${TH} font-mono`}>{money(r.amount)}</td>
                <td className={`${TH} whitespace-nowrap text-right`}>{admin && <>
                  <button type="button" onClick={() => setDlg({ rec: r })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Edit" data-testid={`special-expense-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => del(r)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete" data-testid={`special-expense-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button></>}</td>
              </tr>
            ))}
            {!!exp.expenses.length && <tr className="border-t-2 border-slate-300 font-bold"><td className={TH} colSpan={3}>TOTAL</td><td className={`${TH} font-mono`}>{money(exp.total_expenses)}</td><td /></tr>}
          </tbody>
        </table>
      </div>
      {admin && <ExpenseDialog open={!!dlg} rec={dlg?.rec} coll={coll} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); reload(); }} />}
    </div>
  );
}
